/* eslint-disable */
import React, { FC, useState, useEffect, useContext, useCallback, ReactNode, useMemo } from 'react';
import { ColumnDef, Row } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// Custom components
import {
  MultiColumnFilter,
  SelectColumnFilter,
  TextColumnFilter,
  NumberRangeFilter,
  FileColumnFilter
} from '@/ui/Table/Filters';
import DateColumnFilter from '@/ui/Table/Filters/DateColumnFilter';
import DateYearColumnFilter from '@/ui/Table/Filters/DateYearColumnFilter';
import { sortBasic, sortLink, sortDateTime } from '@/lib/table/sort';
import { filterDate, filterDateYear, filterMulti, filterRange } from '@/lib/table/filter';
import DataControllerSubModel from '@/lib/DataControllerSubModel';
import Table from '@/ui/Table/Table';
import TableGenerator from '@/lib/TableGenerator';
import TableCheckbox from './TableCheckbox';

// Context
import UserContext from '@/context/UserContext/UserContext';

// Models
import radio from '@/models/submodels/radio';
import checkbox from '@/models/submodels/checkbox';
import picker from '@/models/submodels/picker';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import {
  SetFilterFn,
  ISubModelsWithData,
  IColumnInfoState,
  ITableBaseProps,
  AllowRowActionFn,
  RowActionFn,
} from '@/@types/ui/Table';
import { FieldAny, IFieldPickerModel } from '@/@types/models/model';
import { FILE_FILTER_VALUES } from './Filters/FileColumnFilter';

declare module '@tanstack/table-core' {
  interface ColumnMeta<TData, TValue> {
    filterComponent: (props: any) => any;
    isCustomFilterComponent: boolean;
  }
}

export interface IModelTableProps extends ITableBaseProps {
  viewName?: string;
  allowRowAction: AllowRowActionFn;
  onRowAction: RowActionFn;
  onRefresh?: () => void;
  allowSearch?: boolean;
  unselectRow?: boolean;
  disableAdd?: boolean;
  identifier?: string;
  pickerApiPathParams?: {[key: string]: (string | number)}
}

type PickersAnalysisType = {
  done: boolean,
  needData: boolean
};

type PDCStatusType = {
  loaded: boolean;
  error: boolean;
  dict: ISubModelsWithData
}

const ModelTable: FC<IModelTableProps> = (props) => {
  const [pickersAnalysis, setPickersAnalysis] = useState<PickersAnalysisType>({ done: false, needData: false});
  const [pdcStatus, setPdcStatus] = useState<PDCStatusType>({loaded: false, error: false, dict: {}});
  const [tableReady, setTableReady] = useState<boolean>(false);
  const [columnInfo, setColumnInfo] = useState<IColumnInfoState>({
    columns: [],
    hiddenColumnNames: [],
    autoHideColumnNames: [],
  });

  const { t } = useTranslation();
  const userContext = useContext(UserContext);

  const {
    dc,
    viewName,
    records,
    children,
    allowAdd,
    allowColumnPicker,
    allowExport,
    allowExportShape,
    allowExportDxf,
    allowFilter,
    allowSelection,
    allowRowAction,
    allowLegend = false,
    onRowAction,
    onRowClick,
    onDoubleClick,
    onRefresh,
    onLegendClick,
    tableName,
    allowSearch = true,
    addLabel = `${t('buttons.new_n')}`,
    handleAdd,
    title,
    rowSelectedCustom,
    selectedIds,
    onSelectionChange,
    unselectRow,
    getRowStyle,
    size,
    defaultPageSize,
    smallButtons,
    disableControls,
    autoHideFooter,
    disableSmallSearch,
    disableAdd,
    identifier,
    customHeaderControl,
    pickerApiPathParams,
  } = props;

  const viewFields = dc.getViewFields(viewName || '');
  const src = dc.getSource();
  const tableIdentifier = identifier || src + viewName;

  const tg = new TableGenerator(t, onRowAction, allowRowAction);

  useEffect(() => {
    analyzePickers();
   }, [])
 
   useEffect(() => {
     resetReadinessChecks();
   }, [viewName]);

   useEffect(() => {
    if (pickersAnalysis.done === false) {
      analyzePickers();
    } else if (pickersAnalysis.done && pickersAnalysis.needData) {
      loadPickersData();
    } else if (pickersAnalysis.done && pickersAnalysis.needData === false) {
      prepareColumns({});
    }
   }, [pickersAnalysis])
 
   useEffect(() => {
     if (pdcStatus.loaded && pdcStatus.error === false) {
       prepareColumns(pdcStatus.dict);
     }
   }, [pdcStatus]);

   useEffect(() => {
    if (columnInfo) {
      setTableReady(true);
    }
   }, [columnInfo])

  const resetReadinessChecks = () => {
    setPickersAnalysis({ done: false, needData: false});
    setPdcStatus({loaded: false, error: false, dict: {}});
    setTableReady(false);
  }

  const analyzePickers = () => {
    const fieldsWithModel = viewFields.filter(
      (f: any) =>
        [
          'picker',
          'checkbox',
          'radio',
          'boolean',
          'active',
          'iconStatus',
        ].indexOf(f.type) !== -1 && f.hasOwnProperty('subModel')
    ) as Array<IFieldPickerModel>;

    if (fieldsWithModel.length === 0) {
      setPickersAnalysis({ done: true, needData: false});
    } else {
      setPickersAnalysis({ done: true, needData: true});
    }
  }

  const loadPickersData = () => {
    const fieldsWithModel = viewFields.filter(
      (f: any) =>
        [
          'picker',
          'checkbox',
          'radio',
          'boolean',
          'active',
          'iconStatus',
        ].indexOf(f.type) !== -1 && f.hasOwnProperty('subModel')
    ) as Array<IFieldPickerModel>;

    const pdcs: ISubModelsWithData = {};

    fieldsWithModel.forEach((f) => {
      const modelType =
        f.type === 'picker'
          ? picker
          : f.type === 'radio'
          ? radio
          : f.type === 'checkbox'
          ? checkbox
          : null;

      if (modelType !== null) {
        Object.assign(pdcs, {
          [f.source]: new DataControllerSubModel(modelType, f, pickerApiPathParams),
        });
      }
    });

    const getDataPromises = Object.keys(pdcs).map((f) => pdcs[f].GetData());

    Promise.all(getDataPromises)
    .then(() => {
      setPdcStatus({loaded: true, error: false, dict: pdcs});
    })
    .catch(() => {
      setPdcStatus({loaded: true, error: true, dict: pdcs});
    })
    .finally(() => {
    });

  }

  const prepareColumns = (pdc: ISubModelsWithData) => {
    const viewFields = dc.getViewFields(viewName || '');

    const columns: ColumnDef<DCRecord, ReactNode>[] = [];

    viewFields.map((f: any) => {
      const renderFn = tg.generateRenderFunction(f, pdc);
      const sortType = getSortType(f);
      columns.push({
        accessorFn: (row: DCRecord) =>
          renderFn({
            value: row[f.source],
            row,
            subModelsWithData: pdc[f.source]?.records,
          }),
        cell: (props) => (
          <>
            {Array.isArray(props.getValue())
              ? (props.getValue() as unknown[]).join(', ')
              : props.getValue()}
          </>
        ),
        id: f.source,
        header: f.ttoken ? f.ttoken : f.title,
        enableColumnFilter: f.filter || false,
        enableSorting: f.sort === undefined || f.sort,
        sortingFn: sortType,
        filterFn: getFilterCompareFn(f),
        meta: {
          isCustomFilterComponent: f.filter || false,
          filterComponent: getFilterControl(f),
        },
      });
    });

    if (allowSelection == 'many') {
      columns.unshift({
        id: 'select',
        // header: ({ table }: { table: Table<DCRecord> }) => ( // wtf is this shit
        header: ({ table }: { table: import("@tanstack/table-core").Table<DCRecord> }) => {
          // only rows passing the filters, tanstack's own toggle-all also selects the filtered-out ones
          const filteredRows = table.getFilteredRowModel().flatRows;
          const selectedCount = filteredRows.filter((r) => r.getIsSelected()).length;
          const allSelected = filteredRows.length > 0 && selectedCount === filteredRows.length;
          return (
            <TableCheckbox
              {...{
                checked: allSelected,
                indeterminate: selectedCount > 0 && !allSelected,
                onClick: (evt: React.MouseEvent) => evt.stopPropagation(),
                onChange: () => {
                  const next = { ...table.getState().rowSelection };
                  filteredRows.forEach((r) => {
                    if (allSelected) delete next[r.id];
                    else next[r.id] = true;
                  });
                  table.setRowSelection(next);
                },
              }}
            />
          );
        },
        cell: ({ row }: { row: Row<DCRecord> }) => (
          <div className="px-1">
            <TableCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                // the row click toggles too, without this the two cancel out
                onClick: (evt: React.MouseEvent) => evt.stopPropagation(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
        enableSorting: false,
      });
    }

    const hiddenColumnNames = dc.getHiddenViewFieldsNames(viewName || '');
    const autoHideColumnNames = dc.getAutoHideViewFieldsNames(viewName || '');

    setColumnInfo({
      columns,
      hiddenColumnNames,
      autoHideColumnNames,
    });
  };

  /**
   * Function that return the Filter Control based on Field type
   *
   * @param f - Field
   * @returns The column filter component for that Field type
   */
  const getFilterControl = (f: FieldAny) => {
    switch (f.type) {
      case 'text':
      case 'mail':
      case 'link':
      case 'multiline':
        return TextColumnFilter();
      case 'radio':
      case 'boolean':
      case 'iconStatus':
      case 'active':
      case 'picker':
        return SelectColumnFilter(
          pdcStatus.dict,
          f.items,
          t
        );
      case 'date':
      case 'datetime':
        if (!f.filterType || f.filterType === 'default')
          return DateColumnFilter(t);
        else if (f.filterType === 'year') 
          return DateYearColumnFilter(
            pdcStatus.dict,
            f.items,
             t)
      case 'checkbox':
      case 'array':
        return MultiColumnFilter(
          pdcStatus.dict,
          f.items,
          t
        );
      case 'document':
      case 'images': {
        const pickerItems = {
          labels: [
            f.type === 'document' ? 'core:filter.sadrzi_dokument' : 'core:filter.sadrzi_sliku',
            f.type === 'document' ? 'core:filter.ne_sadrzi_dokument' : 'core:filter.ne_sadrzi_sliku',
          ],
            values: [FILE_FILTER_VALUES.CONTAINS, FILE_FILTER_VALUES.NOT_CONTAINS,],
        };

        return FileColumnFilter(pickerItems, t);
      };
      case 'currency':
      case 'wholenum':
      case 'numeric':
        if (f.filterType === 'text') {
          return TextColumnFilter();
        } else {
          return NumberRangeFilter(f.filterStep);
        }
      default:
        return () => null;
    }
  };

  /**
   * Function that return the Filter compare function on Field type
   *
   * @param f - Field
   * @returns The column filter compare function for that Field type
   */
  const getFilterCompareFn = (f: FieldAny) => {
    switch (f.type) {
      case 'radio':
      case 'boolean':
      case 'iconStatus':
        return (row: Row<DCRecord>, columnid: string, currValue: unknown) =>
          row.original[columnid] === currValue;
      case 'picker':
        return (row: Row<DCRecord>, columnid: string, currValue: any) => {
          if (f.multi) {
            return filterMulti(row.original[columnid] as number[], currValue);
          }
          return row.original[columnid] === currValue;
        };
      case 'document':
      case 'images':
        return (row: Row<DCRecord>, columnId: string, currValue: string) => {
          if (!currValue) return true;
          const items = row.original[columnId] as any[] | undefined;

          const value = items && items.length > 0
            ? FILE_FILTER_VALUES.CONTAINS
            : FILE_FILTER_VALUES.NOT_CONTAINS;

          return currValue === value;
        };
      case 'active':
        return (row: Row<DCRecord>, columnid: string, currValue: unknown) =>
          currValue === 'all' || row.original[columnid] === currValue;
      case 'checkbox':
      case 'array':
        return (row: Row<DCRecord>, columnid: string, currValue: any) =>
          filterMulti(row.original[columnid] as any[], currValue);
      case 'date':
      case 'datetime':
        if (!f.filterType || f.filterType==='default') {
          return (row: Row<DCRecord>, columnid: string, currValue: any) =>
            filterDate(row.original[columnid] as unknown as Date, currValue);
        } else if (f.filterType === 'year') {
          return (row: Row<DCRecord>, columnid: string, currValue: any) =>
            filterDateYear(row.original[columnid] as moment.Moment, currValue);
        }
      case 'currency':
      case 'wholenum':
      case 'numeric':
        if (f.filterType === 'text') {
          return (row: Row<DCRecord>, columnid: string, currValue: any) =>
            `${row.original[columnid]}`
              .toUpperCase()
              .indexOf(currValue.toString().toUpperCase()) >= 0;
        } else {
          return (row: Row<DCRecord>, columnid: string, currValue: any) =>
            filterRange(row.original[columnid] as number, currValue);
        }
      default:
        return (row: Row<DCRecord>, columnid: string, currValue: any) =>
          `${row.original[columnid]}`
            .toUpperCase()
            .indexOf(currValue.toUpperCase()) >= 0;
    }
  };

  /**
   * Function that return the Filter sort type on Field type
   *
   * @param f - Field
   * @returns The column filter sort type for that Field type
   */
  const getSortType = (f: FieldAny) => {
    switch (f.type) {
      case 'date':
      case 'datetime':
        return sortDateTime;
      case 'link':
        return sortLink;
      default:
        return sortBasic;
    }
  };

  return tableReady ?
    <Table
      defaultPageSize={defaultPageSize}
      data={records || []}
      columns={columnInfo.columns}
      hiddenColumnNames={columnInfo.hiddenColumnNames}
      autoHideColumnNames={columnInfo.autoHideColumnNames}
      title={title}
      allowColumnPicker={allowColumnPicker}
      allowSelection={allowSelection}
      allowFilter={allowFilter}
      allowExport={allowExport}
      allowExportShape={allowExportShape}
      allowExportDxf={allowExportDxf}
      allowAdd={allowAdd}
      allowSearch={allowSearch}
      allowLegend={allowLegend}
      addLabel={addLabel}
      handleAdd={handleAdd}
      dc={dc}
      tableName={tableName}
      onRowClick={onRowClick}
      onDoubleClick={onDoubleClick}
      onRefresh={onRefresh}
      onLegendClick={onLegendClick}
      identifier={tableIdentifier}
      rowSelectedCustom={rowSelectedCustom}
      selectedIds={selectedIds}
      onSelectionChange={onSelectionChange}
      unselectRow={unselectRow}
      getRowStyle={getRowStyle}
      size={size}
      smallButtons={smallButtons !== undefined ? smallButtons : false}
      disableControls={disableControls !== undefined ? disableControls : false}
      autoHideFooter={autoHideFooter}
      disableSmallSearch={disableSmallSearch}
      disableAdd={disableAdd}
      customHeaderControl={customHeaderControl}
      scrollToRow={{
        rowId: rowSelectedCustom ?? -1
      }}
    >
      {children}
    </Table>
    : null;
};

export default ModelTable;
